<template>
  <main class="home-network-page">
    <header class="network-header">
      <button type="button" class="network-header__close" @click="goBack">
        <app-image :src="closeIcon"></app-image>
      </button>
      <h2>{{ t("wallet.chooseNetwork") }}</h2>
      <span />
    </header>

    <van-loading v-if="loading" class="network-loading" />

    <section v-else class="network-list">
      <div
        v-for="item in networks"
        :key="networkKey(item)"
        class="network-row"
        :class="{ active: isSelected(item) }"
        @click="selectNetwork(item)"
      >
        <app-image :src="networkIcon(item)" alt="" />
        <div data-strong>{{ item.name || item.symbol || "--" }}</div>
        <app-image
          class="check"
          v-if="isSelected(item)"
          :src="checkBlue"
        ></app-image>
      </div>
    </section>

    <footer class="network-footer">
      <van-button
        block
        round
        type="primary"
        :disabled="!selectedNetwork"
        @click="confirmNetwork"
      >
        {{ t("common.confirm") }}
      </van-button>
    </footer>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { showFailToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { chainHotList } from "@/api/wallet";
import { useProfileStore } from "@/stores/profile";
import bnbIcon from "@/assets/images/icon_bnb.png";
import ethIcon from "@/assets/images/icon_ethereum.png";
import checkBlue from "@/assets/images/check_blue.png";
import closeIcon from "@/assets/images/close_no.png";
import fallbackIcon from "@/assets/images/homeImage/network_icon.png";

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();

const loading = ref(false);
const networks = ref([]);
const selectedNetwork = ref(null);

const currentChainKey = computed(() => networkKey(profileStore.currentNetwork));

function networkKey(network = {}) {
  return String(
    network?.chainNum || network?.id || network?.rpcUrl || network?.name || "",
  ).trim();
}

function isSelected(network) {
  return networkKey(network) === networkKey(selectedNetwork.value);
}

function networkIcon(network = {}) {
  if (network?.img) {
    return network.img;
  }
  const keyword =
    `${network?.name || ""} ${network?.symbol || ""} ${network?.coin || ""}`.toUpperCase();
  if (keyword.includes("BNB") || keyword.includes("BSC")) return bnbIcon;
  if (keyword.includes("ETH") || keyword.includes("ETHEREUM")) return ethIcon;
  return fallbackIcon;
}

async function loadNetworks() {
  loading.value = true;
  try {
    if (profileStore.networkList.length) {
      networks.value = profileStore.networkList;
    } else {
      const result = await chainHotList();
      networks.value = Array.isArray(result) ? result : [];
      profileStore.setNetworkList(networks.value);
    }
  } catch (error) {
    networks.value = profileStore.currentNetwork
      ? [profileStore.currentNetwork]
      : [];
  } finally {
    selectedNetwork.value =
      networks.value.find(
        (item) => networkKey(item) === currentChainKey.value,
      ) ||
      profileStore.currentNetwork ||
      networks.value[0] ||
      null;
    loading.value = false;
  }
}

function selectNetwork(network) {
  selectedNetwork.value = network;
}

async function confirmNetwork() {
  if (!selectedNetwork.value) {
    return;
  }

  if (!profileStore.canSupportNetwork(selectedNetwork.value)) {
    showFailToast(t("home.currentAccountNotSupport"));
    return;
  }

  await profileStore.setCurrentNetwork({
    ...selectedNetwork.value,
    currentNode: selectedNetwork.value.currentNode || { status: 1 },
  });
  router.replace("/home/index");
}

function goBack() {
  router.replace("/home/index");
}

onMounted(loadNetworks);
</script>

<style scoped lang="scss">
.home-network-page {
  min-height: var(--app-viewport-height);
  padding-bottom: calc(88px + env(safe-area-inset-bottom));
  background: #ffffff;
}

.network-header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: grid;
  grid-template-columns: 48px 1fr 48px;
  align-items: center;
  height: calc(64px + env(safe-area-inset-top));
  padding: env(safe-area-inset-top) 10px 0;
  background: #ffffff;

  h2 {
    margin: 0;
    color: #000000;
    font-size: 18px;
    line-height: 28px;
    font-weight: 700;
    text-align: center;
  }
}

.network-header__close {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 18px;
    height: 18px;
    display: block;
  }
}

.network-loading {
  display: block;
  margin: 80px auto 0;
}

.network-list {
  // padding-top: 50px;
}

.network-row {
  width: 100%;
  height: 78px;
  display: flex;
  align-items: center;
  padding: 0px 22px;
  border: 0;
  background: #ffffff;
  color: #000000;
  text-align: left;

  &.active {
    background: #e5efff;
  }

  .app-image {
    width: 40px;
    height: 40px;
    margin-right: 10px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  }

  .check {
    width: 20px;
    height: 20px;
  }

  [data-strong] {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 16px;
    line-height: 25px;
    font-weight: 500;
  }

  .van-icon {
    color: #2667ff;
    font-size: 24px;
  }
}

.network-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  padding: 14px 22px calc(14px + env(safe-area-inset-bottom));
  border-top: 1px solid #eeeeee;
  background: #ffffff;

  :deep(.van-button) {
    height: 52px;
    font-size: 18px;
    font-weight: 500;
    background: #2667ff;
    border-color: #2667ff;
    box-shadow: 0 6px 14px rgba(38, 103, 255, 0.28);
  }
}

@media (min-width: 48em) and (hover: hover) and (pointer: fine) {
  .network-footer {
    left: var(--app-shell-left);
    right: auto;
    width: var(--app-shell-width);
    max-width: 100vw;
  }
}
</style>
